import { ServiceLanguage, ServiceLanguageMap } from "@albertine/i18n";
import { MemberSourceId, MemberSourceIdMap } from "@albertine/types";
import { EngagementIndicator } from "@prototypes/engagement-indicator/EngagementIndicator";
import { ScoreIndicator } from "@prototypes/score-indicator/ScoreIndicator";
import { format, parseISO } from "date-fns";
import { ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatProfileDateTime } from "@/utils/format.util";
import routes from "@/utils/routes";
import type { MembershipOption, Profile } from "../data/profile";
import type { MembershipAction, OpenProfileOverlay } from "../overlays/profileOverlay";
import {
    AssigneeRow,
    FieldList,
    FieldRow,
    IconMenu,
    MissingRow,
} from "../profilePrimitives";
import { listMissingLabels } from "../profileUtils";

export function MembershipSection({
    memberId,
    membership,
    membershipTiers,
    canEdit,
    setOverlay,
    onOpenProactivity,
}: {
    memberId: string;
    membership: Profile["membership"];
    membershipTiers: readonly MembershipOption[];
    canEdit: boolean;
    setOverlay: OpenProfileOverlay;
    onOpenProactivity: (messageId: string) => void;
}) {
    const hasMembership = !!membership.tier;
    const tierLabel = membershipTiers.find(
        (option) => option.id === membership.tier,
    )?.name;

    return (
        <div className="flex flex-col gap-3 px-3 pt-5 pb-6">
            <div className="flex flex-col gap-0.5">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                        <h2 className="heading-4! text-zinc-900">
                            {hasMembership ? tierLabel : "Not set"}
                        </h2>
                        {membership.trial && (
                            <Badge variant="secondary" size="sm">
                                Trial
                            </Badge>
                        )}
                        {membership.expired && (
                            <Badge variant="red" size="sm">
                                Expired
                            </Badge>
                        )}
                    </div>
                    {hasMembership ? (
                        <MembershipActionsMenu
                            membership={membership}
                            canEdit={canEdit}
                            setOverlay={setOverlay}
                        />
                    ) : (
                        <Button
                            variant="ghost"
                            className="h-auto px-2 py-1 text-xs"
                            onClick={() =>
                                setOverlay({
                                    type: "membership",
                                    action: "create",
                                })
                            }
                        >
                            Edit
                        </Button>
                    )}
                </div>
                {membership.statusNote ? (
                    <p className="whitespace-nowrap text-xs text-zinc-500 leading-4">
                        {membership.statusNote}
                    </p>
                ) : !hasMembership ? (
                    <p className="whitespace-nowrap text-xs text-zinc-500 leading-4">
                        No active membership
                    </p>
                ) : null}
            </div>

            <AssigneeRow
                name={membership.assignedTo.name}
                agentId={membership.assignedTo.id}
                onEdit={() => setOverlay({ type: "edit", section: "manage" })}
            />

            {membership.groups.length > 0 && (
                <FieldRow label="Groups">
                    <div className="flex flex-wrap gap-1">
                        {membership.groups.map((group) => (
                            <Badge key={group.id} variant="blue" size="sm">
                                {group.name}
                            </Badge>
                        ))}
                    </div>
                </FieldRow>
            )}
            {membership.tenant && (
                <FieldRow label="Tenant">
                    <p className="text-xs text-zinc-900 leading-4">
                        {membership.tenant}
                    </p>
                </FieldRow>
            )}
            <FieldList
                rows={[
                    membership.memberSince
                        ? {
                              label: "Member since",
                              value: format(
                                  parseISO(membership.memberSince),
                                  "d MMM yyyy",
                              ),
                          }
                        : undefined,
                    membership.source
                        ? {
                              label: "Source",
                              value: [
                                  memberSourceLabel(membership.source),
                                  membership.additionalSourceInfo,
                              ]
                                  .filter(Boolean)
                                  .join(", "),
                          }
                        : undefined,
                ].filter((row): row is { label: string; value: string } => !!row)}
            />
            {membership.language && (
                <FieldRow label="Language">
                    <p className="text-xs text-zinc-900 leading-4">
                        {serviceLanguageLabel(membership.language)}
                    </p>
                </FieldRow>
            )}
            {membership.lastSeen && (
                <FieldRow label="Last seen">
                    <p className="text-xs text-zinc-900 leading-4">
                        {formatProfileDateTime(membership.lastSeen)}
                    </p>
                </FieldRow>
            )}
            {membership.scores?.map((score) => (
                <FieldRow key={score.label} label={score.label}>
                    {score.kind === "engagement" ? (
                        <EngagementIndicator
                            lastMessageAt={
                                score.lastMessageAt
                                    ? parseISO(score.lastMessageAt)
                                    : undefined
                            }
                        />
                    ) : (
                        <ScoreIndicator
                            average={score.average}
                            ratingsCount={score.ratingsCount}
                        />
                    )}
                </FieldRow>
            ))}
            <FieldRow label="Proactivity">
                {membership.proactivity?.length ? (
                    <div className="flex flex-col gap-1">
                        {membership.proactivity.map((item) => (
                            <Tooltip key={item.messageId}>
                                <TooltipTrigger
                                    render={
                                        <Button
                                            variant="ghost"
                                            className="h-auto justify-start gap-1.5 px-0 py-0.5"
                                            nativeButton={false}
                                            render={
                                                <a
                                                    href={routes.memberWithMessage(
                                                        memberId,
                                                        item.messageId,
                                                    )}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        onOpenProactivity(
                                                            item.messageId,
                                                        );
                                                    }}
                                                />
                                            }
                                        />
                                    }
                                >
                                    <span className="text-xs text-zinc-900 leading-4">
                                        {formatProfileDateTime(item.sharedAt)}
                                    </span>
                                    <ArrowUpRight className="size-3.5 shrink-0 text-zinc-400" />
                                </TooltipTrigger>
                                <TooltipContent side="top" align="start">
                                    Go to message
                                </TooltipContent>
                            </Tooltip>
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-zinc-500 leading-4">
                        No proactivity yet
                    </p>
                )}
            </FieldRow>
            <MissingRow
                labels={listMissingLabels([
                    { label: "Groups", present: membership.groups.length > 0 },
                    { label: "Source", present: !!membership.source },
                    { label: "Language", present: !!membership.language },
                ])}
            />
        </div>
    );
}

function MembershipActionsMenu({
    membership,
    canEdit,
    setOverlay,
}: {
    membership: Profile["membership"];
    canEdit: boolean;
    setOverlay: OpenProfileOverlay;
}) {
    // A trial without an end date has not started yet, so there is nothing to
    // extend or end — upgrading is the only move. Expiry never narrows the menu.
    const trialActivated = !!membership.trialEndsOn;
    const openOverlay = (action: MembershipAction) => () =>
        setOverlay({ type: "membership", action });

    const editItem = canEdit ? (
        <DropdownMenuItem onClick={openOverlay("edit")}>
            Edit membership
        </DropdownMenuItem>
    ) : null;

    if (!membership.trial) {
        return (
            <IconMenu label="Membership actions">
                <DropdownMenuItem onClick={openOverlay("details")}>
                    Membership details
                </DropdownMenuItem>
                {editItem}
            </IconMenu>
        );
    }

    if (!trialActivated) {
        return (
            <IconMenu label="Membership actions">
                <DropdownMenuItem onClick={openOverlay("upgrade")}>
                    Upgrade to paid member
                </DropdownMenuItem>
            </IconMenu>
        );
    }

    return (
        <IconMenu label="Membership actions">
            <DropdownMenuItem onClick={openOverlay("extendTrial")}>
                Extend trial
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openOverlay("upgrade")}>
                Upgrade to paid member
            </DropdownMenuItem>
            <DropdownMenuItem onClick={openOverlay("endTrial")}>
                End trial membership
            </DropdownMenuItem>
            {editItem}
        </IconMenu>
    );
}

function memberSourceLabel(source: string) {
    const parsed = MemberSourceId.safeParse(source);
    return parsed.success ? MemberSourceIdMap[parsed.data] : source;
}

function serviceLanguageLabel(language: string | undefined) {
    const parsed = ServiceLanguage.safeParse(language);
    return parsed.success ? ServiceLanguageMap[parsed.data] : language;
}
